import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { opsApi } from '@/api/ops';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { CheckboxField, ReasonField, reasonOk } from '@/components/ops/Forms';
import { ConfirmDialog } from '@/components/ops/Modal';
import { MutationError } from '@/components/ops/Problem';

/**
 * "Delete tenant…" (plan hazy-tumbling-graham Stage 2, owner only). Nothing happens to the tenant here: this ASKS for the
 * deletion — a job that waits for ANOTHER owner's approval, then freezes the tenant, backs it up and proves the backup,
 * waits for an owner to type the short name, and only then removes it. The dialog says all of that, and what is kept.
 */
export function DeleteTenantButton({ env, tenantId, name }: { env: string; tenantId: string; name: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [understood, setUnderstood] = useState(false);
  const request = useMutation({
    mutationFn: () => opsApi.tenants.requestDeletion(env, tenantId, reason.trim()),
    onSuccess: (job) => navigate(`/ops/provisioning/jobs/${job.id}`),
  });

  if (!hasPermission(PERMISSIONS.tenantProvision)) return null;

  const close = () => {
    if (request.isPending) return;
    setOpen(false);
    setReason('');
    setUnderstood(false);
    request.reset();
  };

  return (
    <>
      <button type="button" className="btn btn-danger-ghost" onClick={() => setOpen(true)} data-testid="delete-tenant">
        Delete tenant…
      </button>
      {open && (
        <ConfirmDialog
          title={`Delete ${name}?`}
          confirmLabel="Ask for the deletion"
          destructive
          pending={request.isPending}
          confirmDisabled={!reasonOk(reason) || !understood}
          onConfirm={() => request.mutate()}
          onCancel={close}
        >
          <p className="t-body" style={{ margin: 0 }}>
            Nothing happens to the tenant yet. This asks for its deletion, which then runs in order:
          </p>
          <ol className="t-body" style={{ margin: '8px 0', paddingInlineStart: 20 }}>
            <li><strong>Another owner approves</strong> on the job page (within 7 days). You cannot approve your own request.</li>
            <li>The tenant is <strong>frozen</strong>: nobody can sign in, its web addresses stop working, its subscription is suspended.</li>
            <li>The server <strong>backs up every database</strong> of the tenant and proves the backup by restoring it.</li>
            <li>An owner <strong>confirms</strong> by typing the tenant's short name (within 24 hours — otherwise it is unfrozen).</li>
            <li>Billing and SMS/WhatsApp accounts are closed, then <strong>the databases are dropped</strong>. This cannot be undone; the backup stays on the server.</li>
          </ol>
          <p className="t-caption muted" style={{ margin: '0 0 8px' }}>
            Uploaded files stay in storage, and invoices and the billing ledger are kept. Until the confirmation, the deletion can
            be stopped and the tenant comes back online.
          </p>
          <ReasonField value={reason} onChange={setReason} label="Why is this tenant deleted? (recorded on the audit trail)" />
          <CheckboxField checked={understood} onChange={setUnderstood}
            label="I understand that once confirmed, the tenant's data is removed for good (only the backup remains)." />
          <MutationError error={request.error} />
        </ConfirmDialog>
      )}
    </>
  );
}
